import { useState } from 'react'
import { useQuery } from 'react-query'
import {
  Eye,
  Users,
  Search,
  TrendingUp,
  Car,
  Building2,
  Filter,
  Calendar,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  ChevronDown,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { analyticsApi } from '../lib/api'
import { formatNumber, formatPrice } from '../lib/utils'
import { Link } from 'react-router-dom'

const path = import.meta.env.VITE_IMAGE_URL

const PERIODS = [
  { value: '', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'Last 7 Days' },
  { value: 'month', label: 'Last 30 Days' },
]

const FILTER_LABELS = {
  brand: 'Brand',
  model: 'Model',
  manufacturer_slug: 'Manufacturer',
  model_group_slug: 'Model Group',
  body_type: 'Body Type',
  vehicle_type: 'Vehicle Type',
  fuel_type: 'Fuel Type',
  color: 'Color',
  year: 'Year',
  min_year: 'Min Year',
  max_year: 'Max Year',
  min_price: 'Min Price',
  max_price: 'Max Price',
  min_mileage: 'Min Mileage',
  max_mileage: 'Max Mileage',
  engine: 'Engine',
  sale_status: 'Sale Status',
  featured: 'Featured',
}

function StatCard({ title, value, icon: Icon, subtitle, color = 'primary' }) {
  const colorMap = {
    primary: 'bg-primary-50 text-primary-600',
    green: 'bg-green-50 text-green-600',
    blue: 'bg-blue-50 text-blue-600',
    orange: 'bg-orange-50 text-orange-600',
  }

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          </div>
          <div className={`rounded-lg p-3 ${colorMap[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function PeriodSelector({ value, onChange }) {
  return (
    <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-1">
      {PERIODS.map((p) => (
        <button
          key={p.value}
          onClick={() => onChange(p.value)}
          className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
            value === p.value
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  )
}

function SkeletonCard() {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
            <div className="h-8 w-16 animate-pulse rounded bg-gray-200" />
          </div>
          <div className="h-11 w-11 animate-pulse rounded-lg bg-gray-200" />
        </div>
      </CardContent>
    </Card>
  )
}

function SkeletonTable({ rows = 5 }) {
  return (
    <div className="space-y-3">
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <div className="h-10 w-10 animate-pulse rounded-lg bg-gray-200" />
          <div className="flex-1 space-y-1">
            <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
            <div className="h-3 w-20 animate-pulse rounded bg-gray-200" />
          </div>
          <div className="h-4 w-12 animate-pulse rounded bg-gray-200" />
        </div>
      ))}
    </div>
  )
}

function FilterAccordion({ filters }) {
  const [openGroups, setOpenGroups] = useState({})

  const grouped = filters.reduce((acc, f) => {
    const key = f.filter_name
    if (!acc[key]) acc[key] = []
    acc[key].push(f)
    return acc
  }, {})

  const sortedGroups = Object.entries(grouped).sort(
    (a, b) => b[1].reduce((s, f) => s + f.usage_count, 0) - a[1].reduce((s, f) => s + f.usage_count, 0)
  )

  const toggle = (key) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <div className="divide-y divide-gray-100">
      {sortedGroups.map(([filterName, items]) => {
        const isOpen = openGroups[filterName]
        const totalCount = items.reduce((s, f) => s + f.usage_count, 0)

        return (
          <div key={filterName}>
            <button
              onClick={() => toggle(filterName)}
              className="flex w-full items-center justify-between px-1 py-3 text-left transition-colors hover:bg-gray-50 rounded-md"
            >
              <div className="flex items-center gap-2">
                <ChevronDown
                  className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
                <span className="text-sm font-medium text-gray-900">
                  {FILTER_LABELS[filterName] || filterName}
                </span>
                <span className="badge badge-gray text-xs">{items.length} values</span>
              </div>
              <span className="text-sm font-semibold text-gray-700">
                {formatNumber(totalCount)} total
              </span>
            </button>

            {isOpen && (
              <div className="mb-2 ml-6 overflow-hidden rounded-lg border border-gray-100">
                <table className="table">
                  <thead className="table-header bg-gray-50">
                    <tr className="table-row">
                      <th className="table-head text-xs">Value</th>
                      <th className="table-head text-right text-xs">Count</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {items.map((f, i) => (
                      <tr key={i} className="table-row">
                        <td className="table-cell text-sm text-gray-700">
                          {f.display_value || f.filter_value}
                        </td>
                        <td className="table-cell text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-100">
                              <div
                                className="h-full rounded-full bg-primary-400"
                                style={{
                                  width: `${(f.usage_count / items[0].usage_count) * 100}%`,
                                }}
                              />
                            </div>
                            <span className="text-sm font-semibold text-gray-900 tabular-nums">
                              {formatNumber(f.usage_count)}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default function Statistics() {
  const [period, setPeriod] = useState('')

  const { data: overview, isLoading: overviewLoading } = useQuery(
    'analytics-overview',
    () => analyticsApi.getOverview(),
    { select: (d) => d.data }
  )

  const { data: mostViewed, isLoading: viewsLoading } = useQuery(
    ['analytics-most-viewed', period],
    () => analyticsApi.getMostViewed({ limit: 10, period: period || undefined }),
    { select: (d) => d.data }
  )

  const { data: topBrands, isLoading: brandsLoading } = useQuery(
    ['analytics-top-brands', period],
    () => analyticsApi.getTopBrands({ period: period || undefined }),
    { select: (d) => d.data }
  )

  const { data: filterSummary, isLoading: filtersLoading } = useQuery(
    ['analytics-filter-summary', period],
    () => analyticsApi.getFilterSummary({ period: period || undefined }),
    { select: (d) => d.data }
  )

  const { data: topFilters } = useQuery(
    ['analytics-top-filters', period],
    () => analyticsApi.getTopFilters({ period: period || undefined }),
    { select: (d) => d.data }
  )

  const { data: viewsTrend } = useQuery(
    'analytics-views-trend',
    () => analyticsApi.getViewsTrend({ days: 30 }),
    { select: (d) => d.data }
  )

  const maxTrend = viewsTrend ? Math.max(...viewsTrend.map((d) => d.views), 1) : 1

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Statistics</h1>
          <p className="mt-1 text-sm text-gray-600">
            Vehicle views, search trends, and audience insights
          </p>
        </div>
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      {/* Overview Cards */}
      {overviewLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Views"
            value={formatNumber(overview?.total_views || 0)}
            icon={Eye}
            subtitle={`${formatNumber(overview?.today_views || 0)} today`}
            color="primary"
          />
          <StatCard
            title="Unique Visitors"
            value={formatNumber(overview?.unique_visitors || 0)}
            icon={Users}
            subtitle={`${formatNumber(overview?.vehicles_with_views || 0)} vehicles viewed`}
            color="green"
          />
          <StatCard
            title="Total Searches"
            value={formatNumber(overview?.total_searches || 0)}
            icon={Search}
            subtitle="Filter interactions"
            color="blue"
          />
          <StatCard
            title="Weekly Views"
            value={formatNumber(overview?.week_views || 0)}
            icon={TrendingUp}
            subtitle={`${formatNumber(overview?.month_views || 0)} this month`}
            color="orange"
          />
        </div>
      )}

      {/* Views Trend Chart */}
      {viewsTrend && viewsTrend.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-base font-semibold">Views Trend (Last 30 Days)</CardTitle>
            <BarChart3 className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-[3px]" style={{ height: 120 }}>
              {viewsTrend.map((d) => {
                const pct = (d.views / maxTrend) * 100
                const dateStr = new Date(d.date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })
                return (
                  <div
                    key={d.date}
                    className="group relative flex-1"
                    style={{ height: '100%' }}
                  >
                    <div className="absolute bottom-0 w-full rounded-t bg-primary-500 transition-all group-hover:bg-primary-600"
                      style={{ height: `${Math.max(pct, 2)}%` }}
                    />
                    <div className="pointer-events-none absolute -top-10 left-1/2 z-10 hidden -translate-x-1/2 rounded bg-gray-900 px-2 py-1 text-xs text-white shadow group-hover:block whitespace-nowrap">
                      {dateStr}: {d.views}
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="mt-2 flex justify-between text-xs text-gray-400">
              <span>
                {viewsTrend.length > 0 &&
                  new Date(viewsTrend[0].date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })}
              </span>
              <span>
                {viewsTrend.length > 0 &&
                  new Date(viewsTrend[viewsTrend.length - 1].date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Most Viewed Vehicles & Top Brands */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Most Viewed - 2/3 width */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="text-base font-semibold">Most Viewed Vehicles</CardTitle>
            <Eye className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            {viewsLoading ? (
              <SkeletonTable rows={5} />
            ) : !mostViewed || mostViewed.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Car className="h-12 w-12 text-gray-300" />
                <p className="mt-2 text-sm text-gray-500">No view data yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {mostViewed.map((v, idx) => (
                  <Link
                    key={v.id}
                    to={`/vehicles/${v.id}/edit`}
                    className="flex items-center gap-4 rounded-lg p-2 transition-colors hover:bg-gray-50"
                  >
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-gray-500">
                      {idx + 1}
                    </div>
                    {v.thumbnail ? (
                      <img
                        src={`${path}${v.thumbnail}`}
                        alt={v.vehicle_id}
                        className="h-10 w-10 flex-shrink-0 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100">
                        <Car className="h-5 w-5 text-gray-400" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">
                        {v.manufacturer_name} {v.model_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {v.vehicle_id} &middot; {v.year} &middot; {formatPrice(v.price)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-right">
                      <Eye className="h-3.5 w-3.5 text-gray-400" />
                      <span className="text-sm font-semibold text-gray-900">
                        {formatNumber(v.view_count)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Brands - 1/3 width */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="text-base font-semibold">Top Brands by Views</CardTitle>
            <Building2 className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            {brandsLoading ? (
              <SkeletonTable rows={5} />
            ) : !topBrands || topBrands.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Building2 className="h-12 w-12 text-gray-300" />
                <p className="mt-2 text-sm text-gray-500">No brand data yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {topBrands.map((b, idx) => {
                  const maxViews = topBrands[0]?.view_count || 1
                  const pct = (b.view_count / maxViews) * 100
                  return (
                    <div key={b.id} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-400">{idx + 1}</span>
                          {b.logo_url ? (
                            <img
                              src={`${path}${b.logo_url}`}
                              alt={b.name}
                              className="h-5 w-5 object-contain"
                            />
                          ) : null}
                          <span className="text-sm font-medium text-gray-900">{b.name}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-semibold text-gray-900">
                            {formatNumber(b.view_count)}
                          </span>
                          <span className="ml-1 text-xs text-gray-400">
                            ({b.vehicle_count} cars)
                          </span>
                        </div>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                        <div
                          className="h-full rounded-full bg-primary-500 transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filter Analytics */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Filter Summary */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="text-base font-semibold">Filter Usage Summary</CardTitle>
            <Filter className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            {filtersLoading ? (
              <SkeletonTable rows={5} />
            ) : !filterSummary || filterSummary.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Search className="h-12 w-12 text-gray-300" />
                <p className="mt-2 text-sm text-gray-500">No filter usage data yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filterSummary.map((f) => {
                  const maxCount = filterSummary[0]?.usage_count || 1
                  const pct = (f.usage_count / maxCount) * 100
                  return (
                    <div key={f.filter_name} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-gray-700">
                          {FILTER_LABELS[f.filter_name] || f.filter_name}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">
                            {f.unique_values} values
                          </span>
                          <span className="font-semibold text-gray-900">
                            {formatNumber(f.usage_count)}
                          </span>
                        </div>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                        <div
                          className="h-full rounded-full bg-blue-500 transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Filter Values — Grouped Accordion */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="text-base font-semibold">Top Searched Values</CardTitle>
            <Search className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            {!topFilters || topFilters.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Search className="h-12 w-12 text-gray-300" />
                <p className="mt-2 text-sm text-gray-500">No search data yet</p>
              </div>
            ) : (
              <FilterAccordion filters={topFilters} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
